import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, message, Space, Typography, Divider } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Title } = Typography;

interface BulkPushFeaturesModalProps {
  visible: boolean;
  hotelId: number;
  emHotelId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface EmLanguage {
  id: number;
  code: string;
  label: string;
}

const BulkPushFeaturesModal: React.FC<BulkPushFeaturesModalProps> = ({
  visible,
  hotelId,
  emHotelId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [emLanguages, setEmLanguages] = useState<EmLanguage[]>([]);
  const [loading, setLoading] = useState(false);
  const [featureCount, setFeatureCount] = useState(0);
  const [features, setFeatures] = useState<any[]>([]);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);

  useEffect(() => {
    if (visible && hotelId !== undefined) {
      loadEmLanguages();
      loadFeatureData();
    } else if (!visible) {
      // Reset on close
      form.resetFields();
      setFeatures([]);
      setFeatureCount(0);
      setAvailableLanguages([]);
    }
  }, [visible, hotelId]);

  const loadEmLanguages = async () => {
    try {
      const response = await axios.get(`${API_URL}/em-push/languages`);
      setEmLanguages(response.data);
    } catch (error) {
      console.error('Failed to load EM languages:', error);
      message.error('Failed to load EM languages');
    }
  };

  const loadFeatureData = async () => {
    try {
      // Fetch all features for this hotel
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/room-features`);
      const flattenedFeatures = response.data;

      // Group flattened features back into unique features with names objects
      const featureMap = new Map<string, any>();
      const languageCodes = new Set<string>();

      flattenedFeatures.forEach((item: any) => {
        const key = item.mewsId;

        if (!featureMap.has(key)) {
          featureMap.set(key, {
            id: item.id,
            mewsId: item.mewsId,
            isActive: item.isActive,
            classification: item.classification,
            names: {},
            descriptions: {},
            shortNames: {},
          });
        }

        const feature = featureMap.get(key);
        if (item.language) {
          languageCodes.add(item.language);
          if (item.name) feature.names[item.language] = item.name;
          if (item.description) feature.descriptions[item.language] = item.description;
          if (item.shortName) feature.shortNames[item.language] = item.shortName;
        }
      });

      const groupedFeatures = Array.from(featureMap.values());

      setFeatureCount(groupedFeatures.length);
      setFeatures(groupedFeatures.slice(0, 10)); // Show first 10 for preview
      setAvailableLanguages(Array.from(languageCodes));
    } catch (error) {
      console.error('Failed to load features:', error);
      message.error('Failed to load features');
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Build language mappings from form values
      const languageMappings = availableLanguages
        .filter(lang => values[`mapping_${lang}`])
        .map(lang => ({
          mewsLanguage: lang,
          emLanguageId: values[`mapping_${lang}`],
        }));

      if (languageMappings.length === 0) {
        message.warning('Please map at least one language');
        setLoading(false);
        return;
      }

      // Get all unique feature IDs (flattened data has duplicates)
      const allFeaturesResponse = await axios.get(`${API_URL}/hotels/${hotelId}/room-features`);
      const uniqueMewsIds = new Set(allFeaturesResponse.data.map((f: any) => f.mewsId));
      const featureIds = Array.from(uniqueMewsIds);

      // Call bulk push endpoint
      const response = await axios.post(`${API_URL}/em-push/features`, {
        featureIds,
        emHotelId,
        languageMappings: languageMappings.map(m => ({
          sqliteLanguage: m.mewsLanguage,
          emLanguageId: m.emLanguageId,
        })),
      });

      if (response.data.success) {
        message.success(
          `Successfully pushed ${response.data.totalPushed} features to EM!`
        );
        if (response.data.totalFailed > 0) {
          message.warning(`${response.data.totalFailed} features failed to push`);
        }
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Bulk push error:', error);
      message.error(error.response?.data?.message || 'Failed to push features to EM');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Bulk Push All Features to EM"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      style={{ top: 20 }}
      styles={{ body: { padding: '12px 24px 16px' } }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* Summary */}
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f5ff', borderRadius: 6, fontSize: 13 }}>
          <Text strong>Features:</Text> {featureCount} &nbsp;|&nbsp; <Text strong>Languages:</Text> {availableLanguages.join(', ')}
        </div>

        {/* Preview */}
        {features.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: 13 }}>Preview ({features.length} features)</Text>
            <div style={{
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              padding: '6px 12px',
              maxHeight: 150,
              overflowY: 'auto',
              background: '#fafafa',
              marginTop: 4,
            }}>
              {features.map((feature, idx) => {
                const featureName = feature.names && Object.values(feature.names)[0];
                return (
                  <div key={feature.id} style={{ marginBottom: 2, fontSize: 13 }}>
                    <Text>{idx + 1}. {featureName || 'Unnamed Feature'}</Text>
                  </div>
                );
              })}
              {featureCount > 10 && (
                <Text type="secondary" style={{ fontStyle: 'italic', fontSize: 12 }}>
                  ...and {featureCount - 10} more
                </Text>
              )}
            </div>
          </div>
        )}

        <Divider style={{ margin: '12px 0' }} />

        {/* Language Mapping */}
        <Text strong style={{ fontSize: 13 }}>Language Mapping</Text>

        <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, marginTop: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{
                  padding: '6px 12px',
                  background: '#f0f5ff',
                  borderBottom: '1px solid #d9d9d9',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: 13,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  Source Language
                </th>
                <th style={{
                  width: '40px',
                  padding: '6px',
                  background: '#fafafa',
                  borderBottom: '1px solid #d9d9d9',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}></th>
                <th style={{
                  padding: '6px 12px',
                  background: '#f6ffed',
                  borderBottom: '1px solid #d9d9d9',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: 13,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  EM Language
                </th>
              </tr>
            </thead>
            <tbody>
              {availableLanguages.map((lang, index) => (
                <tr key={lang} style={{ borderBottom: index < availableLanguages.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <td style={{ padding: '6px 12px', background: '#fafafa', verticalAlign: 'middle' }}>
                    <Text strong style={{ color: '#1890ff', fontSize: 13 }}>{lang}</Text>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <ArrowRightOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                  </td>
                  <td style={{ padding: '4px 12px', background: '#f6fff6', verticalAlign: 'middle' }}>
                    <Form.Item
                      name={`mapping_${lang}`}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Select EM language"
                        allowClear
                        options={emLanguages.map(emLang => ({
                          value: emLang.id,
                          label: `${emLang.code} (ID: ${emLang.id})`,
                        }))}
                      />
                    </Form.Item>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Push All Features to EM
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BulkPushFeaturesModal;
